import React, { useEffect, useState } from "react";
import { message, Form, Input, Select, Button, Skeleton } from "antd";
import { useListVals } from "../../Firebase/databaseHooks";
import Firebase from "../../Firebase/Firebase";
import FireFetch from "../../Firebase/FireFetch";
import { getDatabase, ref, update } from "firebase/database";
import { useAuthState } from "react-firebase-hooks/auth";
import { getAuth } from "firebase/auth";
import emailjs from "emailjs-com";
import { useAuth } from "../../AuthContext/AuthContext";
import {
	USER_STATUS,
	isUserStatus,
	normalizeUserStatus,
	shouldTriggerApproval,
} from "../../../constants/userStatus";
import { getCallable } from "../../Firebase/functionsClient";
import { filterSelectOption, sortSelectOptions } from "../../utils/selectOptions.js";

const db = getDatabase(Firebase);
const auth = getAuth(Firebase);

const listVolunteerTypesFn = getCallable("listVolunteerTypes");
const approveUserAccountFn = getCallable("approveUserAccount");
const sendPasswordResetLinkFn = getCallable("sendPasswordResetLink");

const getApproveUserErrorMessage = (error) => {
	switch (error?.code) {
		case "functions/invalid-argument":
			return error?.message || "The approval request is missing required fields.";
		case "functions/not-found":
			return "The selected user record was not found.";
		case "functions/failed-precondition":
			return (
				error?.message ||
				"This user cannot be approved yet because the required account data is incomplete."
			);
		case "functions/permission-denied":
			return "You do not have permission to approve this user.";
		case "functions/unauthenticated":
			return "Your session expired. Please sign in again.";
		case "functions/internal":
			return "Approval failed on the server. Check the approveUserAccount function logs.";
		default:
			return error?.message || "Unable to approve user.";
	}
};

const getApprovalOutcomeMessage = (response) => {
	const approvalSucceeded = response?.data?.approvalSucceeded !== false;
	const emailResults = Array.isArray(response?.data?.emailResults)
		? response.data.emailResults
		: [];
	const failedResults = emailResults.filter((result) => result?.sent === false);
	const failureSummary =
		String(response?.data?.emailFailureSummary || "")
			.trim() ||
		failedResults
			.map((result) => {
				const target =
					result?.recipient === "approver"
						? "Approver email"
						: "User email";
				const email = result?.email ? ` (${result.email})` : "";
				const reason = result?.reason || "Unknown delivery error.";
				return `${target}${email}: ${reason}`;
			})
			.join(" ");

	if (!approvalSucceeded) {
		return {
			type: "error",
			text: "User approval did not complete.",
		};
	}

	if (!failedResults.length) {
		return {
			type: "success",
			text: "User approved successfully. All approval emails were sent.",
		};
	}

	return {
		type: "warning",
		text: `User approved successfully, but some approval emails failed. ${failureSummary}`,
	};
};

const EditUser = (props) => {
	const [loggedUser, authLoading] = useAuthState(auth);
	const [editUserForm] = Form.useForm();
	const [showLoading, setShowLoading] = useState(false);
	const { permissionChecker, canAccessModule } = useAuth();
	const canUpdateUserFromModule = canAccessModule("USERS", "update");
	const [arrayUsers, usersLoading] = useListVals(ref(db, "talmidsdb/users"));
	const [countryArray, countriesLoading] = useListVals(ref(db, "talmidsdb/countries"));
	const [languageArray, languagesLoading] = useListVals(ref(db, "talmidsdb/languages"));
	const [communityArray, communitiesLoading] = useListVals(ref(db, "talmidsdb/communities"));
	const [orgArray, orgsLoading] = useListVals(ref(db, "talmidsdb/organisations"));
	const [roleArray, rolesLoading] = useListVals(ref(db, "talmidsdb/userRoles"));
	const [volunteerArray, setVolunteerArray] = useState([]);
	const [volunteerLoading, setVolunteerLoading] = useState(false);
	const [lessonTypes, lessonTypesLoading] = useListVals(ref(db, "talmidsdb/lessonTypes"));
	const [user, setUser] = useState(props.user);

	const hasAny = (codes) =>
		permissionChecker?.hasAnyPermission
			? permissionChecker.hasAnyPermission(codes)
			: false;

	const isE1 = hasAny(["E1"]);
	const isE2 = hasAny(["E2"]);
	const isE3 = hasAny(["E3"]);
	const isR4 = hasAny(["R4"]);
	const isR5 = hasAny(["R5"]);
	const isN = hasAny(["N"]);

	const isSelf = props.user?.id === loggedUser?.uid;

	const canViewSensitive =
		isE1 || isE2 || isE3 || isSelf;

	const canEditAnything = isE1;
	const partialEdit = isE2 || isE3;
	const canEditUser = (canEditAnything || partialEdit) && canUpdateUserFromModule;
	const isReadOnly = isR4 || isR5;
	const canEditEmailPhone = isE1;
	const canEditRoleOrg = isE1;
	const noAccess = isN;
	const currentStatus = Form.useWatch("status", editUserForm);
	const isLookupLoading =
		authLoading ||
		usersLoading ||
		countriesLoading ||
		languagesLoading ||
		communitiesLoading ||
		orgsLoading ||
		rolesLoading ||
		lessonTypesLoading ||
		volunteerLoading;

	useEffect(() => {
		let mergedUser = props.user;
		if (props.user && arrayUsers && arrayUsers.length > 0) {
			const fullUser = arrayUsers.find((u) => u.id === props.user.id) || {};
			mergedUser = { ...props.user, ...fullUser };
		}
		setUser(mergedUser);

		if (!mergedUser) {
			editUserForm.resetFields();
			return;
		}

		editUserForm.resetFields();

		const countriesValue =
			mergedUser.countries && Array.isArray(mergedUser.countries)
				? mergedUser.countries
				: mergedUser.countryID
					? [mergedUser.countryID]
					: [];

		const organisationsValue =
			mergedUser.organisations && Array.isArray(mergedUser.organisations)
				? mergedUser.organisations
				: mergedUser.organisationID
					? [mergedUser.organisationID]
					: [];

		const communitiesValue =
			mergedUser.communities && Array.isArray(mergedUser.communities)
				? mergedUser.communities
				: mergedUser.communityID
					? [mergedUser.communityID]
					: [];

		const lessonsAttendedObj = mergedUser.lessonsAttended || {};
		const lessonsAttendedValue = Object.entries(lessonsAttendedObj)
			.filter(([, lessonsAttended]) => !!lessonsAttended)
			.map(([lessonTypeId]) => lessonTypeId);

		editUserForm.setFieldsValue({
			firstname: mergedUser.firstname || "",
			surname: mergedUser.surname || "",
			phone: canViewSensitive ? mergedUser.phone || "" : "Hidden",
			email: canViewSensitive ? mergedUser.email || "" : "Hidden",
			preferredLanguage: mergedUser.preferredLanguage || undefined,
			supervisor: mergedUser.supervisor || undefined,
			countries: countriesValue,
			organisations: organisationsValue,
			communities: canViewSensitive ? communitiesValue : [],
			volunteerType: Array.isArray(mergedUser.volunteerType)
				? mergedUser.volunteerType
				: mergedUser.volunteerType
					? [mergedUser.volunteerType]
					: [],
			role: mergedUser.userRole || undefined,
			status: normalizeUserStatus(mergedUser.userStatus) || undefined,
			password: mergedUser.password || "",
			lessonsAttended: lessonsAttendedValue || "",
		});
	}, [props.user, arrayUsers, loggedUser, canViewSensitive, editUserForm]);

	useEffect(() => {
		let isMounted = true;

		const loadVolunteerTypes = async () => {
			if (authLoading) return;
			if (!loggedUser) {
				if (isMounted) {
					setVolunteerArray([]);
					setVolunteerLoading(false);
				}
				return;
			}

			setVolunteerLoading(true);
			try {
				const res = await listVolunteerTypesFn({});
				const types = Array.isArray(res?.data?.volunteerTypes) ? res.data.volunteerTypes : [];
				if (isMounted) {
					setVolunteerArray(types);
				}
			} catch (_error) {
				if (isMounted) {
					setVolunteerArray([]);
				}
			} finally {
				if (isMounted) {
					setVolunteerLoading(false);
				}
			}
		};

		loadVolunteerTypes();

		return () => {
			isMounted = false;
		};
	}, [authLoading, loggedUser]);

	if (isLookupLoading) {
		return <Skeleton active paragraph={{ rows: 12 }} />;
	}

	const editUser = async (values) => {
		if (!canEditUser || isReadOnly || noAccess || !canUpdateUserFromModule) {
			message.error("You do not have permission to edit this user.");
			return;
		}

		setShowLoading(true);

		const countries = values.countries || [];
		const organisations = values.organisations || [];
		const communities = values.communities || [];
		const selectedLanguage = languageArray?.find(
			(item) => item.id === values.preferredLanguage
		);
		const selectedLessonIds = values.lessonsAttended || [];
		const existingLessonsAttended = user?.lessonsAttended || {};

		const allLessonTypeIds = new Set([
			...(lessonTypes?.map((lt) => lt?.id).filter(Boolean) || []),
			...Object.keys(existingLessonsAttended),
			...selectedLessonIds,
		]);

		const lessonsAttended = {};
		allLessonTypeIds.forEach((id) => {
			lessonsAttended[id] = selectedLessonIds.includes(id);
		});

		const nextStatus = normalizeUserStatus(values.status) || "";
		const shouldApprove = shouldTriggerApproval(user?.userStatus, nextStatus);

		const object = {
			firstname: values.firstname,
			surname: values.surname,
			email: values.email,
			phone: values.phone,
			preferredLanguage: values.preferredLanguage || "",
			preferredLanguageName:
				selectedLanguage?.languageName || selectedLanguage?.language || "",
			supervisor: values.supervisor,
			countries,
			organisations,
			communities,
			countryID: countries[0] || "",
			organisationID: organisations[0] || "",
			communityID: communities[0] || "",
			userRole: values.role,
			volunteerType: values.volunteerType,
			userStatus: shouldApprove ? normalizeUserStatus(user?.userStatus) || "" : nextStatus,
			lessonsAttended,
		};
		if (!isE1) {
			delete object.email;
			delete object.phone;
			delete object.userRole;
			delete object.organisations;
			delete object.organisationID;
		}

		Object.entries(object).forEach(([key, value]) => {
			if (value === undefined) {
				object[key] = "";
			}
			if (value === "Hidden") {
				delete object[key];
			}
		});

		if (!user?.id) {
			setShowLoading(false);
			message.error(
				"This object's ID is undefined! Contact the admin for assistance."
			);
			return;
		}

		try {
			await update(ref(db, "talmidsdb/users/" + user.id), object);
			FireFetch.addLog("Users", user.id, "Edit");

			let updatedUser = {
				...user,
				...object,
			};

			if (shouldApprove) {
				const response = await approveUserAccountFn({ userId: user.id });
				FireFetch.addLog("Users", user.id, "Approve");
				updatedUser = {
					...updatedUser,
					userStatus: USER_STATUS.ACTIVE,
					approvedAt: response?.data?.approvedAt || new Date().toISOString(),
					approvedBy: loggedUser?.uid || user?.approvedBy || "",
					approvalEmailFailureSummary: response?.data?.emailFailureSummary || "",
					approvalEmailResults: response?.data?.emailResults || [],
				};
				const approvalOutcome = getApprovalOutcomeMessage(response);
				message[approvalOutcome.type](approvalOutcome.text);
			} else {
				message.success("User updated successfully");
			}

			if (props.onUserUpdated) {
				props.onUserUpdated(updatedUser);
			}
			setUser(updatedUser);
			props.modal(false);
		} catch (error) {
			message.error(shouldApprove ? getApproveUserErrorMessage(error) : "Unable to update user: " + error);
		} finally {
			setShowLoading(false);
		}
	};

	const resetPassword = async () => {
		if (!canEditAnything || noAccess || !canUpdateUserFromModule) {
			message.error("You do not have permission to reset passwords.");
			return;
		}
		if (!user) return;

		const role = user.userRole;

		if (role === "ComVol") {
			// keep your ComVol logic as-is
			message.info(
				"A new random password will be sent to the supervisor. Please use it as a one time password!"
			);

			const chars =
				"ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz1234567890@#$%^&*";
			const password = Array(10)
				.fill(chars)
				.map((x) => x[Math.floor(Math.random() * x.length)])
				.join("");

			await update(ref(db, "talmidsdb/users/" + user.id), { password });

			const supervisorEmail = arrayUsers?.find((x) => x.id === user.supervisor)?.email;

			emailjs
				.send("service_v2ya563", "template_m4alwzb", {
					user_name: `${user.firstname} ${user.surname}`,
					user_password: password,
					supervisor_email: supervisorEmail,
				})
				.then(
					() => message.success("Password reset email sent!"),
					(error) =>
						message.error("Unable to send reset password link :: " + error.message)
				);

			return;
		}

		// Non-ComVol: call your callable function to email the reset link
		const email = String(user.email || "").trim();
		const regexEmail = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

		if (!regexEmail.test(email)) {
			message.error("User email is missing or invalid.");
			return;
		}

		try {
			message.loading({ content: "Sending reset link...", key: "resetLink" });

			await sendPasswordResetLinkFn({ email });

			message.success({ content: "Password reset email sent!", key: "resetLink" });
		} catch (err) {
			const msg =
				err?.message ||
				err?.details ||
				"Unable to send reset password link. Please try again.";
			message.error({ content: msg, key: "resetLink" });
		}
	};

	const approveUser = async () => {
		if (!canEditAnything || isReadOnly || noAccess || !canUpdateUserFromModule) {
			message.error("You do not have permission to approve users.");
			return;
		}
		if (!user?.id) {
			message.error("This user's ID is missing.");
			return;
		}

		try {
			const values = await editUserForm.validateFields();
			await editUser({
				...values,
				status: USER_STATUS.ACTIVE,
			});
		} catch (error) {
			// Ant Design renders field-level validation errors itself.
			if (!error?.errorFields) {
				message.error(getApproveUserErrorMessage(error));
			}
		}
	};

	if ((noAccess && !isE1 && !partialEdit) || !canUpdateUserFromModule) {
		return <div>You do not have permission to view this page.</div>;
	}

	return (
		<>
			<Form layout="vertical" form={editUserForm} onFinish={editUser}>
				<Form.Item
					label="First name"
					name="firstname"
					rules={[{ required: true, message: "Please input first name!" }]}
				>
					<Input
						placeholder="enter new user firstname"
						id="firstname"
						disabled={!partialEdit ? (isReadOnly || !canEditAnything) : false}
					/>
				</Form.Item>

				<Form.Item
					label="Surname"
					name="surname"
					rules={[{ required: true, message: "Please input surname!" }]}
				>
					<Input
						placeholder="enter new user surname"
						id="surname"
						disabled={!partialEdit ? (isReadOnly || !canEditAnything) : false}
					/>
				</Form.Item>

				<Form.Item label="Phone" name="phone">
					<Input
						type="phone"
						disabled={
							(!canViewSensitive || !canEditEmailPhone || isReadOnly || partialEdit) && !canEditAnything
						}
						placeholder="enter new user phoneNumber"
						id="phone"
					/>
				</Form.Item>

				<Form.Item label="Email" name="email">
					<Input
						type="email"
						disabled={
							(!canViewSensitive || !canEditEmailPhone || isReadOnly || partialEdit) && !canEditAnything
						}
						placeholder="enter new user email"
						id="email"
					/>
				</Form.Item>

				<Form.Item
					label="Preferred Language"
					name="preferredLanguage"
					id="preferredLanguage"
					rules={[{ required: true, message: "Please select preferred language!" }]}
				>
					<Select
						placeholder="Select preferred language"
						showSearch
						disabled={!partialEdit ? (isReadOnly || !canEditAnything) : false}
						optionFilterProp="children"
						filterOption={filterSelectOption}
						filterSort={sortSelectOptions}
					>
						{languageArray?.map((item) => (
							<Select.Option key={item.id} value={item.id}>
								{item.languageName || item.language}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item label="Supervisor" name="supervisor">
					<Select
						placeholder="Select user supervisor"
						showSearch
						disabled={!partialEdit ? (isReadOnly || !canEditAnything) : false}
						optionFilterProp="children"
						filterOption={filterSelectOption}
						filterSort={sortSelectOptions}
					>
						{arrayUsers?.map((item) => (
							<Select.Option key={item.id} value={item.id}>
								{`${item.firstname} ${item.surname}`}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					label="User Countries"
					name="countries"
					rules={[{
						required: true,
						type: "array",
						min: 1,
						message: "Please select at least one country!",
					}]}
				>
					<Select
						placeholder="Select user countries"
						mode="multiple"
						disabled={!partialEdit ? (isReadOnly || !canEditAnything) : false}
					>
						{countryArray?.map((item) => (
							<Select.Option key={item.id} value={item.id}>
								{item.countryName}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item label="Organisations" name="organisations">
					<Select
						placeholder="Select user organisations (optional)"
						mode="multiple"
						disabled={(isReadOnly || !canEditRoleOrg || partialEdit) && !canEditAnything}
						showSearch
						optionFilterProp="children"
						filterOption={filterSelectOption}
						filterSort={sortSelectOptions}
					>
						{orgArray?.map((item) => (
							<Select.Option key={item.id} value={item.id}>
								{item.name}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					label="Communities / Regions"
					name="communities"
					rules={[{
						required: true,
						type: "array",
						min: 1,
						message: "Please select at least one community!",
					}]}
				>
					<Select
						placeholder="Select user communities (optional)"
						mode="multiple"
						showSearch
						disabled={!partialEdit ? (isReadOnly || !canEditAnything || !canViewSensitive) : false}
						optionFilterProp="children"
						filterOption={filterSelectOption}
						filterSort={sortSelectOptions}
					>
						{communityArray?.map((item) => (
							<Select.Option key={item.id} value={item.id}>
								{item.name}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item label="Volunteer Type" name="volunteerType">
					<Select
						placeholder="Select volunteer type"
						mode="multiple"
						loading={volunteerLoading || authLoading}
						disabled={(!partialEdit ? (isReadOnly || !canEditAnything) : false) || volunteerLoading || authLoading}
					>
						{volunteerArray?.map((item, index) => (
							<Select.Option key={index} value={item.id}>
								{item.volunteerType}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item
					label="User Role"
					name="role"
					rules={[{ required: true, message: "Please select a user role!" }]}
				>
					<Select
						placeholder="Select user role"
						disabled={(isReadOnly || !canEditRoleOrg || partialEdit) && !canEditAnything}
					>
						{roleArray?.map((item, index) => (
							<Select.Option key={index} value={item.userRoleID}>
								{item.roleName}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				<Form.Item label="User status" name="status">
					<Select
						placeholder="Select user status"
						disabled={!partialEdit ? (isReadOnly || !canEditAnything) : false}
					>
						{[
							USER_STATUS.AWAITING_APPROVAL,
							USER_STATUS.ACTIVE,
							USER_STATUS.NOT_ACTIVE,
							USER_STATUS.SUSPENDED,
							USER_STATUS.DISABLED,
						].map(
							(item) => (
								<Select.Option key={item} value={item}>
									{item}
								</Select.Option>
							)
						)}
					</Select>
				</Form.Item>

				<Form.Item label="Lesson Type" name="lessonsAttended" id="lessonsAttended">
					<Select
						mode="multiple"
						placeholder="Select lessons the user has attended"
						showSearch
						disabled={!partialEdit ? (isReadOnly || !canEditAnything) : false}
						optionFilterProp="children"
						filterOption={filterSelectOption}
					>
						{lessonTypes?.map((lt) => (
							<Select.Option key={lt.id} value={lt.id}>
								{lt.typeName || "Untitled lesson"}
							</Select.Option>
						))}
					</Select>
				</Form.Item>

				{/* Buttons only if user can edit */}
				{canEditAnything && !isReadOnly && (
					<div className="d-flex flex-row gap-2">
						{isUserStatus(currentStatus, USER_STATUS.AWAITING_APPROVAL) && (
							<Button className="mx-1" onClick={approveUser} loading={showLoading}>
								Approve
							</Button>
						)}
						<Button
							className="mx-1"
							type="primary"
							htmlType="submit"
							loading={showLoading}
						>
							Save
						</Button>

						<Button className="mx-1" onClick={resetPassword}>
							Reset Password
						</Button>

						<Button className="mx-1" onClick={() => props.modal(false)}>
							Close
						</Button>
					</div>
				)}

				{(!canEditAnything || isReadOnly) && (
					<div className="d-flex flex-row gap-2">
						<Button className="mx-1" onClick={() => props.modal(false)}>
							Close
						</Button>
					</div>
				)}
			</Form>
		</>
	);
};

export default EditUser;
